import PrintContentButton from "@/components/print-content-button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import InboxPlacementPublishButton from "../inboxplacement-publish-button";
import { PlacementType } from "@/types/inboxPlacements.types";
import { usePlacementContext } from "./placement-context";
import { useGetTestResult } from "@/hooks/use-inboxplacement";
import { ProcessingProgress } from "@/components/ui/processing-progress";
import { useState } from "react";

const PlacementHeader = () => {
  const { state, setState } = usePlacementContext();
  const { data, isLoading, isSuccess } = useGetTestResult(
    state.params.email,
    state.params.type,
    state.params.testId
  );

  return (
    <div className="flex flex-col md:flex-row md:justify-between md:items-start items-center justify-center gap-3">
      <div className="text-center md:text-left">
        <div className=" flex items-center gap-3">
          <h2 className="text-2xl font-semibold">Test Results</h2>
          {isLoading ? (
            <Skeleton className="h-6 w-24" />
          ) : data?.test?.isCompleted ? (
            <Badge
              variant="outline"
              className="bg-green-100 text-green-600 border-green-600 dark:bg-green-900/20 dark:text-green-400 dark:border-green-400"
            >
              Completed
            </Badge>
          ) : (
            <Badge
              variant="outline"
              className="bg-blue-100 text-blue-600 border-blue-600 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-400"
            >
              In Progress
            </Badge>
          )}
        </div>
        {isLoading ? (
          <Skeleton className="h-5 w-64 my-1" />
        ) : (
          <p className="text-muted-foreground text-sm md:text-base">
            {state.params.email}
          </p>
        )}
      </div>
      <div className="flex flex-col md:items-end items-center gap-2">
        <div className="flex items-center gap-2">
          {isLoading ? (
            <div className="flex gap-2">
              <Skeleton className="h-9 w-9" />
              <Skeleton className="h-9 w-24" />
            </div>
          ) : (
            isSuccess && (
              <>
                <PrintContentButton
                  id="placement-test-print"
                  type="inbox-placement"
                  preRunCallback={() => {
                    setState((prev) => ({
                      ...prev,
                      isPrinting: true,
                    }));
                    window.cioanalytics?.track("inbox_placement_printed", {
                      email: data?.test?.senderEmail || "",
                      type: state.params.type,
                    });
                  }}
                  postRunCallback={() => {
                    setState((prev) => ({
                      ...prev,
                      isPrinting: false,
                    }));
                  }}
                  triggerClassName="[&>span]:hidden sm:[&>span]:block"
                  fileName={state.params.email}
                />

                {!state.isPrinting && (
                  <InboxPlacementPublishButton
                    ids={[data?.test?.id || ""]}
                    companyId={data?.test?.companyId || ""}
                    email={data?.test?.senderEmail || ""}
                    isPublic={data?.test?.isPublic || false}
                    type={"details-page"}
                    reportType={
                      state.params.type === PlacementType.B2C
                        ? PlacementType.B2C
                        : PlacementType.B2B
                    }
                  />
                )}
              </>
            )
          )}
        </div>

        {isLoading ? (
          <div className="flex flex-col gap-1 items-center md:items-end">
            <Skeleton className="h-4 w-40" />
          </div>
        ) : data?.test?.completedAt ? (
          <p className="text-muted-foreground space-x-1 text-center md:text-left text-sm">
            <span className="text-foreground">Completed on:</span>
            <span>
              {data?.test?.completedAt
                ? new Date(data?.test?.completedAt).toLocaleString("en-GB", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true, // This enables AM/PM format
                  })
                : "Not available"}
            </span>
          </p>
        ) : (
          <p className="text-muted-foreground space-x-1 text-center md:text-left text-sm">
            <span className="text-foreground">Created on:</span>
            <span>
              {data?.test?.sentAt
                ? new Date(data?.test?.sentAt).toLocaleString("en-GB", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true, // This enables AM/PM format
                  })
                : "Not available"}
            </span>
          </p>
        )}
      </div>
    </div>
  );
};

export const PlacementLoadingIndicator = () => {
  const { state, isNotCompleted, summary } = usePlacementContext();
  const [showIndicator, setShowIndicator] = useState(true);
  return (
    showIndicator && (
      <ProcessingProgress
        testId={state.params.testId}
        onTimeout={() => {
          setShowIndicator(false);
        }}
        isNotCompleted={isNotCompleted}
        summary={summary}
      />
    )
  );
};

export default PlacementHeader;
